import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { ArrowLeft, ChevronRight } from 'lucide-react';

import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Questionnaire } from '@/components/questionnaire/Questionnaire';
import { SketchUploader } from '@/components/sketches/SketchUploader';
import { SketchList } from '@/components/sketches/SketchList';
import { useAuth } from '@/hooks/useAuth';
import { useProject } from '@/hooks/useProject';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { sectionNumber } from '@/lib/utils';
import { ROUTES } from '@/lib/constants';
import { usePublicSettings } from '@/hooks/usePublicSettings';

type Phase = 'questionnaire' | 'sketches';

/**
 * /projects/:projectId/wizard
 *
 * Two-phase flow inside one page:
 *   (1) Questionnaire — 14-step wizard with live preview
 *   (2) Sketches      — upload up to N sketches (N = page_count from Q11)
 *
 * On phase 2 completion, the user clicks "Start AURA" which:
 *   - calls jobs.startJob mutation
 *   - navigates to /jobs/<jobId> (pipeline page)
 */
export function ProjectWizardPage() {
  const { projectId: rawId } = useParams<{ projectId: string }>();
  const projectId = rawId as Id<'projects'> | undefined;
  const project = useProject(projectId ?? null);

  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Local phase — initialised from project.status once it loads.
  const [phase, setPhase] = useState<Phase>('questionnaire');
  const [phaseInitialized, setPhaseInitialized] = useState(false);

  // Sketches list — needed for the validation check on Start AURA.
  const sketches = useQuery(
    api.sketches.listForProject,
    token && projectId ? { token, projectId } : 'skip',
  );

  // Questionnaire row — needed for the page_count check on Start AURA.
  const qa = useQuery(
    api.questionnaire.get,
    token && projectId ? { token, projectId } : 'skip',
  );

  const startJob = useMutation(api.jobs.startJob);
  const [starting, setStarting] = useState(false);

  // Pick the correct phase from project.status once the project document loads.
  useEffect(() => {
    if (!project || phaseInitialized) return;
    if (project.status === 'sketches' || project.status === 'pipeline') {
      setPhase('sketches');
    } else {
      setPhase('questionnaire');
    }
    setPhaseInitialized(true);
  }, [project, phaseInitialized]);

  // Redirect if the user already left the wizard (same tab / deep link).
  useEffect(() => {
    if (!project) return;
    if (project.status === 'completed' && project.currentJobId) {
      navigate(ROUTES.jobDelivery(project.currentJobId), { replace: true });
    } else if (project.status === 'completed' && !project.currentJobId) {
      navigate(ROUTES.projects, { replace: true });
    } else if (project.status === 'pipeline' && project.currentJobId) {
      navigate(ROUTES.jobPipeline(project.currentJobId), { replace: true });
    } else if (project.status === 'failed' && project.currentJobId) {
      navigate(ROUTES.jobPipeline(project.currentJobId), { replace: true });
    }
  }, [project, navigate]);

  // Loading + not-found states.
  if (!projectId) {
    return (
      <PageShell>
        <p className="text-center text-vermilion">Missing project id in URL.</p>
      </PageShell>
    );
  }
  if (project === undefined) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingSpinner label="Loading project…" />
        </div>
      </PageShell>
    );
  }
  if (project === null) {
    return (
      <PageShell>
        <div className="mx-auto max-w-xl pt-12 text-center">
          <p className="font-serif text-display-sm italic text-ink">Project not found.</p>
          <p className="mt-3 text-sm text-graphite">
            It may have been deleted, or you may not have access.
          </p>
          <Button asChild className="mt-6">
            <a href={ROUTES.projects}>Back to projects</a>
          </Button>
        </div>
      </PageShell>
    );
  }

  if (
    project.status === 'completed' ||
    project.status === 'pipeline' ||
    (project.status === 'failed' && project.currentJobId)
  ) {
    return null;
  }

  // Phase 2 readiness check.
  const declaredPageCount = qa?.answers?.page_count ?? 0;
  const sketchCount = sketches?.length ?? 0;
  const sketchesReady = declaredPageCount > 0 && sketchCount === declaredPageCount;
  const allSketchesLabeled = (sketches ?? []).every((s) => s.label.trim().length > 0);

  // Handlers.
  const onQuestionnaireComplete = () => setPhase('sketches');
  const onBackToQuestionnaire = () => setPhase('questionnaire');

  const onStartAura = async () => {
    if (!token) return;
    if (!sketchesReady) {
      toast({
        variant: 'destructive',
        title: 'Sketch count mismatch',
        description: `You declared ${declaredPageCount} page${declaredPageCount === 1 ? '' : 's'} in the questionnaire and uploaded ${sketchCount}.`,
      });
      return;
    }
    if (!allSketchesLabeled) {
      toast({
        variant: 'destructive',
        title: 'Label every sketch',
        description: 'Each sketch needs a short label like "home" or "about".',
      });
      return;
    }
    setStarting(true);
    try {
      const newJobId = await startJob({ token, projectId });
      navigate(ROUTES.jobPipeline(newJobId));
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not start the pipeline',
        description: friendlyAuthError(err),
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <PageShell>
      {/* Page header */}
      <header className="mb-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="marginalia">
            <span className="mr-2">{sectionNumber(phase === 'questionnaire' ? 1 : 2)}</span>
            {phase === 'questionnaire' ? 'questionnaire' : 'sketches'}
          </p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-graphite">
                project
              </p>
              <h1 className="mt-1 font-serif text-display-md italic text-ink">
                {project.name}
              </h1>
              {project.description ? (
                <p className="mt-3 max-w-xl text-sm text-graphite leading-relaxed">
                  {project.description}
                </p>
              ) : null}
            </div>

            {/* Phase indicator */}
            <ol className="flex items-center gap-3 font-mono text-[11px]">
              <li
                className={
                  phase === 'questionnaire' ? 'text-ink' : 'text-graphite line-through'
                }
              >
                01 · questionnaire
              </li>
              <ChevronRight className="h-3 w-3 text-graphite" />
              <li className={phase === 'sketches' ? 'text-ink' : 'text-graphite'}>
                02 · sketches
              </li>
              <ChevronRight className="h-3 w-3 text-graphite" />
              <li className="text-graphite">03 · pipeline</li>
            </ol>
          </div>
        </div>
      </header>

      {/* Phase body */}
      {phase === 'questionnaire' ? (
        <Questionnaire projectId={projectId} onComplete={onQuestionnaireComplete} />
      ) : (
        <SketchPhase
          projectId={projectId}
          sketches={sketches ?? []}
          declaredPageCount={declaredPageCount}
          sketchesReady={sketchesReady}
          allSketchesLabeled={allSketchesLabeled}
          starting={starting}
          onStartAura={onStartAura}
          onBack={onBackToQuestionnaire}
        />
      )}
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Sketch phase composition (kept inline for cohesion)
// ─────────────────────────────────────────────────────────────────────────

function SketchPhase({
  projectId,
  sketches,
  declaredPageCount,
  sketchesReady,
  allSketchesLabeled,
  starting,
  onStartAura,
  onBack,
}: {
  projectId: Id<'projects'>;
  sketches: { _id: string; label: string }[];
  declaredPageCount: number;
  sketchesReady: boolean;
  allSketchesLabeled: boolean;
  starting: boolean;
  onStartAura: () => void;
  onBack: () => void;
}) {
  const { maxSketchMb } = usePublicSettings();
  return (
    <div className="grid grid-cols-12 gap-6 lg:gap-10">
      {/* Spacer to align with stepper column */}
      <div className="hidden lg:col-span-2 lg:block" />

      <div className="col-span-12 lg:col-span-10">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Uploader */}
          <section>
            <h2 className="font-serif text-display-sm italic text-ink">
              Upload your lo-fi sketches.
            </h2>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-graphite">
              One sketch per page. PNG or JPG, under {maxSketchMb}MB each. SCRY (the
              vision agent) reads each one and produces a component tree.
              You declared{' '}
              <span className="font-mono text-ink">{declaredPageCount || '—'}</span>{' '}
              page{declaredPageCount === 1 ? '' : 's'} in the questionnaire.
            </p>
            <div className="mt-8">
              <SketchUploader
                projectId={projectId}
                currentCount={sketches.length}
                maxSketches={Math.max(declaredPageCount, 1)}
              />
            </div>
          </section>

          {/* List */}
          <section>
            <h2 className="font-serif text-display-sm italic text-ink">
              Your sketches.
            </h2>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-graphite">
              Reorder them to match how the user will encounter them. Click
              a label to rename — use names like "home", "about", "pricing".
            </p>
            <div className="mt-8">
              <SketchList projectId={projectId} />
            </div>
          </section>
        </div>

        {/* Start AURA */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6">
          <Button variant="ghost" onClick={onBack} disabled={starting}>
            <ArrowLeft className="h-4 w-4" />
            Back to questionnaire
          </Button>

          <p className="font-mono text-[11px] text-graphite">
            {sketchesReady && allSketchesLabeled
              ? 'ready'
              : !sketchesReady
                ? `${sketches.length} of ${declaredPageCount || '?'} sketches uploaded`
                : 'label every sketch first'}
          </p>

          <Button
            onClick={onStartAura}
            disabled={!sketchesReady || !allSketchesLabeled || starting}
            variant="accent"
            size="lg"
          >
            {starting ? 'Starting AURA…' : 'Start AURA'}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
